import type {
    AudiobookItem,
    BookDetail,
    BookListItem,
    Course,
    PlaylistVideo,
    VideoSeries
} from '@/types'
import type { BookReviews } from '@/lib/reviews'

import { absoluteUrl, siteConfig } from '@/constants/site'
import { contact, director, kurani, socials } from '@/constants/germa'
import { defaultLocale, localePath, locales, toLocale } from '@/i18n/config'
import { getMessages } from '@/i18n'
import { lessonPathForVideo } from '@/lib/video-lessons'

export type JsonLd = Record<string, unknown>

/**
 * Absolute URL when the site address is configured, otherwise the plain path.
 * Search engines and AI crawlers resolve relative JSON-LD references against
 * the page they were found on, so the markup stays valid either way.
 */
const ref = (path: string): string => absoluteUrl(path) || path

/** A page's address in a portal language (/de/libra), absolute when the site address is set. */
const pageRef = (path: string, locale: string = defaultLocale): string => ref(localePath(path, toLocale(locale)))

const root = (): string => {
    const base = ref('/')
    return base.endsWith('/') ? base : `${base}/`
}

export const ORGANIZATION_ID = `${root()}#organization`
export const WEBSITE_ID = `${root()}#website`

const languageMap: Record<string, string> = {
    shqip: 'sq',
    gjermanisht: 'de',
    anglisht: 'en',
    arabisht: 'ar',
    turqisht: 'tr',
    spanjisht: 'es',
    boshnjakisht: 'bs'
}

export const toLanguageCode = (language: string | null): string => {
    const key = (language ?? '').trim().toLowerCase()
    return languageMap[key] ?? 'sq'
}

export const organizationSchema = (locale: string = defaultLocale): JsonLd => ({
    '@type': ['Organization', 'EducationalOrganization'],
    '@id': ORGANIZATION_ID,
    name: siteConfig.name,
    alternateName: 'Germa',
    url: pageRef('/', locale),
    description: getMessages(locale).meta.siteDescription,
    email: contact.email,
    inLanguage: toLocale(locale),
    logo: {
        '@type': 'ImageObject',
        url: ref('/logo.webp'),
        caption: siteConfig.name
    },
    image: siteConfig.ogImage,
    founder: {
        '@type': 'Person',
        '@id': `${ref('/rreth-nesh')}#drejtori`,
        name: director.name,
        jobTitle: getMessages(locale).about.directorRole
    },
    knowsLanguage: locales,
    sameAs: socials.map((s) => s.href).filter((href) => href.startsWith('http')),
    contactPoint: {
        '@type': 'ContactPoint',
        contactType: 'customer support',
        email: contact.email,
        availableLanguage: ['sq', 'de', 'en']
    }
})

export const websiteSchema = (locale: string = defaultLocale): JsonLd => ({
    '@type': 'WebSite',
    '@id': WEBSITE_ID,
    url: pageRef('/', locale),
    name: siteConfig.name,
    description: getMessages(locale).meta.siteDescription,
    inLanguage: locales,
    publisher: { '@id': ORGANIZATION_ID }
})

export const breadcrumbSchema = (
    trail: { name: string; path: string }[],
    locale: string = defaultLocale
): JsonLd => ({
    '@type': 'BreadcrumbList',
    itemListElement: [{ name: getMessages(locale).common.home, path: '/' }, ...trail].map((item, index) => ({
        '@type': 'ListItem',
        position: index + 1,
        name: item.name,
        item: pageRef(item.path, locale)
    }))
})

export const bookSchema = (book: BookDetail, locale: string = defaultLocale, reviews?: BookReviews): JsonLd => {
    const schema: JsonLd = {
        '@type': 'Book',
        '@id': `${ref(`/libra/${book.slug}`)}#book`,
        name: book.title,
        url: pageRef(`/libra/${book.slug}`, locale),
        bookFormat: 'https://schema.org/Paperback',
        inLanguage: toLanguageCode(book.language),
        publisher: { '@id': ORGANIZATION_ID },
        author: {
            '@type': book.author ? 'Person' : 'Organization',
            name: book.author ?? siteConfig.name
        }
    }

    if (book.subtitle) schema.alternativeHeadline = book.subtitle
    if (book.excerpt || book.description) schema.description = book.excerpt ?? book.description
    if (book.cover) schema.image = book.cover
    if (book.category) schema.genre = book.category
    if (book.readUrl) {
        schema.potentialAction = {
            '@type': 'ReadAction',
            target: book.readUrl
        }
    }
    // Only approved reviews, which are shown on the page.
    if (reviews && reviews.summary.count > 0) {
        schema.aggregateRating = {
            '@type': 'AggregateRating',
            ratingValue: reviews.summary.average,
            ratingCount: reviews.summary.count,
            bestRating: 5,
            worstRating: 1
        }
        schema.review = reviews.items.slice(0, 5).map((review) => ({
            '@type': 'Review',
            author: { '@type': 'Person', name: review.name },
            datePublished: review.createdAt.slice(0, 10),
            reviewRating: { '@type': 'Rating', ratingValue: review.rating, bestRating: 5, worstRating: 1 },
            ...(review.comment ? { reviewBody: review.comment } : {})
        }))
    }

    return schema
}

export const bookListSchema = (books: BookListItem[], name: string, locale: string = defaultLocale): JsonLd => ({
    '@type': 'ItemList',
    name,
    numberOfItems: books.length,
    itemListElement: books.map((book, index) => ({
        '@type': 'ListItem',
        position: index + 1,
        item: {
            '@type': 'Book',
            '@id': `${ref(`/libra/${book.slug}`)}#book`,
            name: book.title,
            url: pageRef(`/libra/${book.slug}`, locale),
            inLanguage: toLanguageCode(book.language),
            ...(book.author ? { author: { '@type': 'Person', name: book.author } } : {}),
            ...(book.cover ? { image: book.cover } : {}),
            publisher: { '@id': ORGANIZATION_ID }
        }
    }))
})

export const courseSchema = (course: Course, locale: string = defaultLocale): JsonLd => {
    const online = course.format.toLowerCase().includes('online')
    const onsite = course.format.toLowerCase().includes('sallë')

    const modes: string[] = []
    if (online) modes.push('online')
    if (onsite) modes.push('onsite')

    return {
        '@type': 'Course',
        '@id': `${ref('/kurse')}#${course.id}`,
        name: course.title,
        description: course.description,
        url: pageRef(`/kurse#${course.id}`, locale),
        inLanguage: 'sq',
        educationalLevel: course.level,
        provider: { '@id': ORGANIZATION_ID },
        hasCourseInstance: {
            '@type': 'CourseInstance',
            courseMode: modes.length > 0 ? modes : 'online',
            inLanguage: 'sq',
            ...(course.schedule ? { description: `Orari: ${course.schedule}` } : {}),
            ...(course.instructor
                ? { instructor: { '@type': 'Person', name: course.instructor } }
                : {})
        }
    }
}

export const videoSchema = (
    video: PlaylistVideo,
    series: VideoSeries,
    locale: string = defaultLocale,
    meta?: { uploadDate?: string | null; duration?: string | null; description?: string | null }
): JsonLd => {
    const path = lessonPathForVideo(series, video.id)
    return {
        '@type': 'VideoObject',
        '@id': `${ref(path)}#video`,
        name: `${video.title} — ${series.title}`,
        description: meta?.description?.slice(0, 500) || series.intro,
        thumbnailUrl: [
            `https://i.ytimg.com/vi/${video.id}/maxresdefault.jpg`,
            `https://i.ytimg.com/vi/${video.id}/hqdefault.jpg`
        ],
        embedUrl: `https://www.youtube.com/embed/${video.id}`,
        contentUrl: `https://www.youtube.com/watch?v=${video.id}`,
        ...(meta?.uploadDate ? { uploadDate: meta.uploadDate } : {}),
        ...(meta?.duration ? { duration: meta.duration } : {}),
        inLanguage: series.id,
        isFamilyFriendly: true,
        isAccessibleForFree: true,
        learningResourceType: 'Video lesson',
        publisher: { '@id': ORGANIZATION_ID },
        url: pageRef(path, locale),
        isPartOf: {
            '@type': 'Course',
            '@id': `${ref('/videoleksione')}#${series.id}`,
            name: series.title
        }
    }
}

export const videoSeriesSchema = (series: VideoSeries, locale: string = defaultLocale): JsonLd => ({
    '@type': 'Course',
    '@id': `${ref('/videoleksione')}#${series.id}`,
    name: series.title,
    description: series.intro,
    url: pageRef(`/videoleksione#${series.anchor}`, locale),
    inLanguage: series.id,
    isAccessibleForFree: true,
    educationalLevel: 'Fillestar',
    teaches: 'Leximi i shkronjave të Kuranit',
    provider: { '@id': ORGANIZATION_ID },
    hasCourseInstance: {
        '@type': 'CourseInstance',
        courseMode: 'online',
        inLanguage: series.id,
        courseWorkload: `PT${series.videos.length * 10}M`
    }
})

/** One audiobook, on its own page (/audiolibra/<slug>). */
export const audiobookSchema = (item: AudiobookItem, locale: string = defaultLocale): JsonLd => ({
    '@type': 'Audiobook',
    '@id': `${ref(`/audiolibra/${item.slug}`)}#audiobook`,
    name: item.title,
    url: pageRef(`/audiolibra/${item.slug}`, locale),
    inLanguage: 'sq',
    isAccessibleForFree: true,
    publisher: { '@id': ORGANIZATION_ID },
    ...(item.description ? { description: item.description } : {}),
    ...(item.cover ? { image: item.cover } : {}),
    ...(item.narrator ? { author: { '@type': 'Person', name: item.narrator } } : {}),
    ...(item.audioUrl
        ? { audio: { '@type': 'AudioObject', contentUrl: item.audioUrl, encodingFormat: 'audio/mpeg' } }
        : {}),
    ...(item.bookSlug ? { isBasedOn: { '@id': `${ref(`/libra/${item.bookSlug}`)}#book` } } : {})
})

export const audiobookListSchema = (
    audiobooks: AudiobookItem[],
    name: string = getMessages(defaultLocale).audiobooks.listName,
    locale: string = defaultLocale
): JsonLd => ({
    '@type': 'ItemList',
    name,
    numberOfItems: audiobooks.length,
    itemListElement: audiobooks.map((item, index) => ({
        '@type': 'ListItem',
        position: index + 1,
        url: pageRef(`/audiolibra/${item.slug}`, locale),
        name: item.title
    }))
})

export const kuraniProjectSchema = (): JsonLd => ({
    '@type': 'WebSite',
    '@id': `${kurani.url}/#website`,
    url: kurani.url,
    name: 'Kurani.info',
    description: kurani.purpose,
    inLanguage: ['sq', 'ar'],
    isAccessibleForFree: true,
    publisher: { '@id': ORGANIZATION_ID },
    about: {
        '@type': 'Book',
        name: 'Kurani Fisnik',
        alternateName: 'Al-Qurʼān',
        inLanguage: ['ar', 'sq'],
        translator: {
            '@type': 'Person',
            name: kurani.translator.name,
            jobTitle: kurani.translator.role
        },
        contributor: kurani.team.map((member) => ({
            '@type': 'Person',
            name: member.name,
            jobTitle: member.role
        }))
    }
})

export const personSchema = (locale: string = defaultLocale): JsonLd => ({
    '@type': 'Person',
    '@id': `${ref('/rreth-nesh')}#drejtori`,
    name: director.name,
    jobTitle: getMessages(locale).about.directorRole,
    description: getMessages(locale).about.directorBio[0],
    image: ref(director.photo),
    worksFor: { '@id': ORGANIZATION_ID },
    alumniOf: director.credentials
        .filter((c) => c.toLowerCase().includes('university'))
        .map((name) => ({ '@type': 'CollegeOrUniversity', name }))
})

/** Wraps a set of entities into a single @graph document. */
export const graph = (...nodes: (JsonLd | null | undefined)[]): JsonLd => ({
    '@context': 'https://schema.org',
    '@graph': nodes.filter(Boolean) as JsonLd[]
})

